// @flow
import { propEq, slice, move, pipe, map } from 'ramda';

import PlanogramItem, { type RawDataItem } from './planogram-item';
import PlanogramData, { type IPlanogramData, type Contains } from './planogram-data';
import { createGrid } from './create-grid';
import { createPlanogramItem } from './create-planogram-item';


const isVisibleItem = ({ width, height }: PlanogramItem): boolean => width > 0 && height > 0;


interface IPlanogramDataExt<T: PlanogramItem> extends IPlanogramData<T> {
  duplicate(rawItems?: Array<RawDataItem>): IPlanogramData<T>,
  canBeMergedLeft(column: number, row: number): boolean,
  canBeMergedRight(column: number, row: number): boolean,
  canBeSplitted(column: number, row: number): boolean,
  getMergedLeft(column: number, row: number | Array<T>): Array<T>,
  getMergedRight(column: number, row: number | Array<T>): Array<T>,
  getSplitted(column: number, row: number | Array<T>): Array<T>,
  getMoved(indexSource: number, indexDestination: number, row: number | Array<T>): Array<T>
}

export default class PlanogramDataExt<T: PlanogramItem = PlanogramItem>
  extends PlanogramData<T>
  implements IPlanogramDataExt<T> {
  constructor(
    items?: Array<Array<T>> = [],
    naming?: Array<Array<string>>,
    contains?: Array<Array<Contains>>,
  ) {
    super(items, naming, contains);
  }

  duplicate(rawItems?: Array<RawDataItem>): PlanogramDataExt<T> {
    const grid: Array<Array<T>> = rawItems ? pipe(
      map(createPlanogramItem),
      x => createGrid<T>(x),
    )(rawItems) : this.items;

    return new PlanogramDataExt(grid, this.naming, this.contains);
  }

  canBeMergedLeft(column: number, row: number): boolean {
    const currentRow = this.getRow(row);
    const sourceItem = this.findItemInRow(item => propEq('column', column, item) && isVisibleItem(item), currentRow);
    if (!sourceItem || column === 0) return false;
    const { width, column: sourceColumn } = sourceItem;
    const index = this.findIndexInRow(item => propEq('column', sourceColumn, item) && isVisibleItem(item), currentRow);
    const { amount = 0 } = this.contains[row][column] || {};
    const { amount: previosCellAmount = 0 } = this.contains[row][column - 1] || {};

    return (!amount
      && width === 1
      && index > 0
      && currentRow[index - 1].width === 1
      && !previosCellAmount
    );
  }

  canBeMergedRight(column: number, row: number): boolean {
    const currentRow = this.getRow(row);
    const sourceItem = this.findItemInRow(item => propEq('column', column, item) && isVisibleItem(item), currentRow);
    if (!sourceItem || column === this.columns - 1) return false;
    const { width, column: sourceColumn } = sourceItem;
    const index = this.findIndexInRow(item => propEq('column', sourceColumn, item) && isVisibleItem(item), currentRow);
    const { amount = 0 } = this.contains[row][column] || {};
    const { amount: nextCellAmount = 0 } = this.contains[row][column + 1] || {};

    return (!amount
      && width === 1
      && index < currentRow.length - 1
      && currentRow[index + 1].width === 1
      && !nextCellAmount
    );
  }

  canBeSplitted(column: number, row: number): boolean {
    const currentRow = this.getRow(row);
    const sourceItem = this.findItemInRow(item => propEq('column', column, item) && isVisibleItem(item), currentRow);
    if (!sourceItem) return false;
    const { width } = sourceItem;
    const { amount = 0 } = this.contains[row][column] || {};
    return (!amount && width === 2);
  }

  getMergedLeft(column: number, row: number | Array<T>): Array<T> {
    const currentRow = this.getRow(row);
    const sourceIndex = this.findIndexInRow(item => propEq('column', column, item) && isVisibleItem(item), currentRow);
    const targetIndex = sourceIndex - 1;
    if (sourceIndex === -1 || targetIndex < 0) {
      console.error('👻 PlanorgamDataExt.getMergedLeft, не найдены изменяемые элементы');
      return [];
    }

    const removed = currentRow[targetIndex].duplicate(() => ({
      width: 0,
      height: 0,
    }));

    const merged = currentRow[sourceIndex].duplicate(() => ({
      width: 2,
      column: currentRow[targetIndex].column,
    }));

    return [removed, merged];
  }

  getMergedRight(column: number, row: number | Array<T>): Array<T> {
    const currentRow = this.getRow(row);
    const sourceIndex = this.findIndexInRow(item => propEq('column', column, item) && isVisibleItem(item), currentRow);
    const targetIndex = sourceIndex + 1;
    if (sourceIndex === -1 || targetIndex >= currentRow.length) {
      console.error('👻 PlanorgamDataExt.getMergedRight, не найдены изменяемые элементы');
      return [];
    }

    const removed = currentRow[targetIndex].duplicate(() => ({
      width: 0,
      height: 0,
    }));

    const merged = currentRow[sourceIndex].duplicate(() => ({
      width: 2,
    }));

    return [removed, merged];
  }

  getSplitted(column: number, row: number | Array<T>): Array<T> {
    const currentRow = this.getRow(row);
    const sourceItem = this.findItemInRow(
      item => propEq('column', column, item) && isVisibleItem(item),
      currentRow,
    );
    if (!sourceItem) {
      console.error('👻 PlanorgamData.getSplitted, не найдены изменяемый элемент');
      return [];
    }

    const hiddenItem = this.findItemInRow(
      item => (
        !isVisibleItem(item)
        && item.column >= sourceItem.column
        && item.column < sourceItem.column + sourceItem.width
      ),
      currentRow,
    );

    if (!hiddenItem) {
      console.error('👻 PlanorgamData.getSplitted, не найдена скрытая ячейка');
      return [];
    }

    if (hiddenItem.column === sourceItem.column) {
      const leftItem = hiddenItem.duplicate(() => ({
        width: 1,
        height: 1,
      }));

      const rightItem = sourceItem.duplicate(() => ({
        column: sourceItem.column + 1,
        width: 1,
        height: 1,
      }));

      return [leftItem, rightItem];
    }

    const leftItem = sourceItem.duplicate(() => ({
      width: 1,
      height: 1,
    }));

    const rightItem = hiddenItem.duplicate(() => ({
      column: sourceItem.column + 1,
      width: 1,
      height: 1,
    }));

    return [leftItem, rightItem];
  }

  getMoved(
    indexSource: number,
    indexDestination: number,
    row: number | Array<T>,
  ): Array<T> {
    const currentRow = this.getRow(row);

    const workingSegmentStartIndex = Math.min(indexDestination, indexSource);
    const workingSegmentFinalIndex = Math.max(indexDestination, indexSource) + 1;
    const workingSegment = slice(workingSegmentStartIndex, workingSegmentFinalIndex, currentRow);

    const workingSegmentStartColumnNumber = workingSegment[0].column;

    const workingSegmentMoved = (
      indexDestination < indexSource
        ? move(-1, 0, workingSegment)
        : move(0, -1, workingSegment)
    );

    const workingSegmentColumnsRemapped = PlanogramData.updateColumnProp(
      workingSegmentStartColumnNumber,
      workingSegmentMoved,
    );

    const workingSegmentNamesUpdated = this.getChangedNames(
      workingSegmentStartIndex,
      workingSegmentColumnsRemapped,
    );

    return workingSegmentNamesUpdated;
  }
}
